// Hold-to-confirm delete modal. Two-stage safety: after the initial "Are you
// sure?" dialog, the user must press-and-hold a big red button for 10 full
// seconds. Releasing before the timer completes resets the progress. Cancel
// is available at any time until the timer fires. After the timer completes
// the parent's `onConfirmed` fires and the actual delete happens — with NO
// undo, because the user held the button for 10 seconds on purpose.
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, X, ShieldAlert } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const HOLD_MS = 10_000;

export default function HoldToDeleteModal({
  open,
  title,
  body,
  itemLabel,          // e.g. "Emma de Vries" — echoed in the description
  onConfirmed,        // fired ONCE after user held for 10s
  onCancel,           // fired when the user cancels / closes
  testid = "hold-delete",
}) {
  const { t } = useI18n();
  const [progress, setProgress] = useState(0);
  const [holding, setHolding] = useState(false);
  const [done, setDone] = useState(false);
  const startedAt = useRef(null);
  const rafRef = useRef(0);
  const firedRef = useRef(false);

  // Reset internal state whenever the modal is (re)opened
  useEffect(() => {
    if (open) {
      setProgress(0); setHolding(false); setDone(false);
      startedAt.current = null; firedRef.current = false;
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [open]);

  const stopHold = () => {
    if (done) return;
    setHolding(false);
    startedAt.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    // Smoothly snap the bar back to 0 so the user gets visual "reset" feedback
    setProgress(0);
  };

  const tick = () => {
    if (!startedAt.current) return;
    const elapsed = performance.now() - startedAt.current;
    const pct = Math.min(1, elapsed / HOLD_MS);
    setProgress(pct);
    if (pct >= 1) {
      if (!firedRef.current) {
        firedRef.current = true;
        setDone(true);
        setHolding(false);
        onConfirmed?.();
      }
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const startHold = () => {
    if (done) return;
    setHolding(true);
    startedAt.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  };

  const secondsLeft = Math.max(0, Math.ceil((HOLD_MS * (1 - progress)) / 1000));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-md grid place-items-center p-4"
          onClick={done ? undefined : onCancel}
          data-testid={testid}
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="card w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "var(--shadow-xl)" }}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] grid place-items-center shrink-0">
                <ShieldAlert size={20} strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-[19px] font-bold tracking-tight text-[hsl(var(--text-strong))]">
                  {title || t("hold_delete_title") || "Definitief verwijderen"}
                </h3>
                <p className="text-[13px] text-[hsl(var(--text-muted))] mt-1.5 leading-relaxed">
                  {body || (t("hold_delete_body") || "Houd de knop 10 seconden vast. Zodra de balk vol is, is de verwijdering definitief — dit kan daarna niet meer ongedaan gemaakt worden.")}
                </p>
                {itemLabel && (
                  <div
                    className="mt-3 inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] text-[12px] font-bold"
                    data-testid={`${testid}-item-label`}
                  >
                    <AlertTriangle size={12} strokeWidth={2.4} />
                    {itemLabel}
                  </div>
                )}
              </div>
              {!done && (
                <button
                  onClick={onCancel}
                  className="w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))] shrink-0"
                  aria-label={t("cancel")}
                  data-testid={`${testid}-close`}
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Hold button — the meat of the modal */}
            <button
              type="button"
              onMouseDown={startHold}
              onMouseUp={stopHold}
              onMouseLeave={stopHold}
              onTouchStart={(e) => { e.preventDefault(); startHold(); }}
              onTouchEnd={stopHold}
              onTouchCancel={stopHold}
              disabled={done}
              data-testid={`${testid}-hold-btn`}
              className={`relative w-full h-14 mt-6 rounded-2xl overflow-hidden select-none transition-transform ${done ? "cursor-default" : holding ? "scale-[0.985]" : "hover:scale-[1.005]"}`}
              style={{
                background: done ? "hsl(var(--danger))" : "hsl(var(--danger) / 0.15)",
                border: `1.5px solid hsl(var(--danger))`,
              }}
            >
              {/* Progress fill */}
              <span
                className="absolute inset-y-0 left-0"
                style={{
                  width: `${progress * 100}%`,
                  background: "hsl(var(--danger))",
                  transition: holding ? "none" : "width 220ms ease-out",
                }}
                data-testid={`${testid}-progress`}
              />
              {/* Label */}
              <span
                className={`relative z-10 flex items-center justify-center gap-2 h-full font-bold text-[14px] tracking-tight ${progress > 0.45 || done ? "text-white" : "text-[hsl(var(--danger))]"}`}
              >
                {done
                  ? (t("hold_delete_done") || "Verwijderd")
                  : holding
                    ? (t("hold_delete_holding") || "Vasthouden…") + " " + secondsLeft + "s"
                    : (t("hold_delete_press") || "Houd 10 seconden ingedrukt om te verwijderen")}
              </span>
            </button>

            {/* Helper row */}
            <div className="flex items-center justify-between gap-3 mt-4">
              <div className="text-[11.5px] text-[hsl(var(--text-muted))] leading-snug flex-1">
                {done
                  ? (t("hold_delete_done_hint") || "Klaar — deze actie kan niet meer ongedaan worden gemaakt.")
                  : holding
                    ? (t("hold_delete_hold_hint") || "Laat je los, dan begint de teller opnieuw.")
                    : (t("hold_delete_idle_hint") || "Loslaat = teller reset. Klik ‘Annuleren’ om terug te gaan.")}
              </div>
              {!done && (
                <button
                  onClick={onCancel}
                  className="btn btn-quiet shrink-0"
                  data-testid={`${testid}-cancel`}
                >
                  {t("cancel")}
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
